"use client";

import { BookingOption, type BookingOptionProps } from "./booking-option";
import { CheckoutPanel } from "./checkout-panel";
import { CheckoutSteps } from "./checkout-steps";
import type { TermsSettings, CustomTerms } from "./utils/types";

export type BookingOptionsGridProps = {
  /** Array of booking options to display */
  options: BookingOptionProps[];
  /** Course title */
  title?: string;
  /** Course thumbnail URL */
  thumbnail?: string;
  /** Currency code */
  currency?: string;
  /** Whether the course is online */
  isOnline?: boolean;
  /** Cancellation policy URL */
  cancellationPolicyUrl?: string;
  /** Terms settings for dialog display */
  termsSettings?: TermsSettings;
  /** Custom terms for dialog display */
  customTerms?: CustomTerms;
  /** Timezone for date formatting */
  timezone?: string;
  /** Source identifier */
  source?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Error message */
  error?: string | null;
  /** Header title */
  headerTitle?: string;
  /** Header description */
  headerDescription?: string;
  /** Callback for joining waiting list */
  onJoinWaitingList?: () => void;
  /** Callback for webcomponents to open terms dialog in external portal */
  onOpenTermsDialog?: (data: {
    termsSettings?: TermsSettings;
    customTerms?: CustomTerms;
    timezone?: string;
    ticketPolicies?: Array<{ ticketName: string; policy: any }>;
  }) => void;
  /** Show the bundle/membership helper (widget-only, basket-enabled). */
  showBundleMembershipHelp?: boolean;
};

/**
 * Skeleton loader for booking options
 */
function BookingOptionsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {[1, 2, 3].map((index) => (
        <div
          key={index}
          className="bg-card rounded-lg p-3 sm:p-4 animate-pulse border border-border"
        >
          <div className="h-32 w-full bg-muted rounded" />
        </div>
      ))}
    </div>
  );
}

/**
 * Booking options grid component.
 * Displays a grid of booking options within a checkout panel.
 * This is a presentation component - it expects pre-computed options.
 */
export function BookingOptionsGrid({
  options,
  title,
  thumbnail,
  currency = "GBP",
  isOnline = false,
  cancellationPolicyUrl = "/terms",
  termsSettings,
  customTerms,
  timezone,
  source = "widget",
  isLoading = false,
  error = null,
  headerTitle = "Choose a booking option",
  headerDescription = "Choose an option you'd like to book.",
  onJoinWaitingList,
  onOpenTermsDialog,
  showBundleMembershipHelp = false,
}: BookingOptionsGridProps) {
  // Show loading state
  if (isLoading) {
    return (
      <CheckoutPanel
        cancellationPolicyUrl={cancellationPolicyUrl}
        termsSettings={termsSettings}
        customTerms={customTerms}
        timezone={timezone}
        onOpenTermsDialog={onOpenTermsDialog}
        title={title}
        thumbnail={thumbnail}
        currency={currency}
        isOnline={isOnline}
        source={source}
        showBundleMembershipHelp={showBundleMembershipHelp}
      >
        <CheckoutSteps.Header
          title={headerTitle}
          description={headerDescription}
        />
        <BookingOptionsSkeleton />
      </CheckoutPanel>
    );
  }

  // Show error state
  if (error) {
    return (
      <CheckoutPanel
        cancellationPolicyUrl={cancellationPolicyUrl}
        termsSettings={termsSettings}
        customTerms={customTerms}
        timezone={timezone}
        onOpenTermsDialog={onOpenTermsDialog}
        title={title}
        thumbnail={thumbnail}
        currency={currency}
        isOnline={isOnline}
        source={source}
        showBundleMembershipHelp={showBundleMembershipHelp}
      >
        <div className="text-center text-destructive-text p-4">{error}</div>
      </CheckoutPanel>
    );
  }

  return (
    <CheckoutPanel
      cancellationPolicyUrl={cancellationPolicyUrl}
      termsSettings={termsSettings}
      customTerms={customTerms}
      timezone={timezone}
      onOpenTermsDialog={onOpenTermsDialog}
      title={title}
      thumbnail={thumbnail}
      currency={currency}
      isOnline={isOnline}
      source={source}
      showBundleMembershipHelp={showBundleMembershipHelp}
    >
      <CheckoutSteps.Header
        title={headerTitle}
        description={headerDescription}
      />
      {options.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-6">
          <p className="text-center text-muted-foreground">
            No booking options available
          </p>
          {onJoinWaitingList && (
            <div className="w-full max-w-sm rounded-lg bg-primary/10 p-4 text-center">
              <p className="text-sm text-foreground mb-3">
                Want to be notified when new dates become available?
              </p>
              <button
                onClick={onJoinWaitingList}
                className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Join Waiting List
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {options.map((option, index) => (
            <div key={index}>
              <BookingOption {...option} currency={currency} />
            </div>
          ))}
        </div>
      )}
    </CheckoutPanel>
  );
}
